'use client';

import { useState } from 'react';
import { Video, Trash2, X } from 'lucide-react';
import { GroupVideo } from '@/types';
import { useLanguage } from '@/context/LanguageContext';
import { getVideoEmbedUrl } from '@/utils';

const baseUrl = '/api/strapi';

interface VideosTabProps {
  groupDocumentId: string;
  videos: GroupVideo[];
  canEdit: boolean;
  onUpdate: (videos: GroupVideo[]) => void;
}

export function VideosTab({ groupDocumentId, videos, canEdit, onUpdate }: VideosTabProps) {
  const { t } = useLanguage();
  const [showModal, setShowModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleAddVideo = async () => {
    if (!videoUrl.trim() || submitting) return;
    if (!getVideoEmbedUrl(videoUrl)) {
      alert(t.groupTabs.invalidYoutubeUrl || 'Please enter a valid YouTube or Vimeo URL');
      return;
    }
    setSubmitting(true);
    try {
      const newVideo: GroupVideo = { url: videoUrl.trim(), title: videoTitle.trim() || undefined };
      const updatedVideos = [...videos, newVideo];

      // Fetch the current group data to get all editable fields
      const getRes = await fetch(`${baseUrl}/groups/${groupDocumentId}`);
      if (!getRes.ok) throw new Error('Failed to fetch group');
      const groupData = await getRes.json();

      // Extract only the editable fields (exclude id, documentId, createdAt, updatedAt, publishedAt, and relations)
      const { id, documentId, createdAt, updatedAt, publishedAt, locale, localizations, forum_topics, volunteer_opportunities, events, group_members, coverImage, ...editableFields } = groupData.data;

      // Update with all editable fields to prevent Strapi from deleting anything
      const res = await fetch(`${baseUrl}/groups/${groupDocumentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            // Include all editable fields from the current group
            ...editableFields,
            // Override only the videos field
            videos: updatedVideos,
          }
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        console.error('❌ Failed to update group:', errorData);
        throw new Error('Failed to add video');
      }

      onUpdate(updatedVideos);
      setShowModal(false);
      setVideoUrl('');
      setVideoTitle('');
    } catch (error) {
      console.error('Failed to add video:', error);
      alert('Failed to add video');
    }
    setSubmitting(false);
  };

  const handleDeleteVideo = async (index: number) => {
    try {
      const updatedVideos = videos.filter((_, i) => i !== index);

      // Fetch the current group data to get all editable fields
      const getRes = await fetch(`${baseUrl}/groups/${groupDocumentId}`);
      if (!getRes.ok) throw new Error('Failed to fetch group');
      const groupData = await getRes.json();

      // Extract only the editable fields (exclude id, documentId, createdAt, updatedAt, publishedAt)
      const { id, documentId, createdAt, updatedAt, publishedAt, locale, localizations, ...editableFields } = groupData.data;

      // Update with all editable fields to prevent Strapi from deleting anything
      const res = await fetch(`${baseUrl}/groups/${groupDocumentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            // Include all editable fields from the current group
            ...editableFields,
            // Override only the videos field
            videos: updatedVideos,
          }
        }),
      });
      if (!res.ok) throw new Error('Failed to delete video');
      onUpdate(updatedVideos);
    } catch (error) {
      console.error('Failed to delete video:', error);
      alert('Failed to delete video');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground" data-testid="group-videos-tab-heading">{t.groupTabs.videos}</h2>
        {canEdit && (
          <button onClick={() => setShowModal(true)} data-testid="group-videos-tab-add-video-btn" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition">
            {t.groupTabs.addVideo}
          </button>
        )}
      </div>

      {videos.length === 0 ? (
        <div data-testid="group-videos-tab-empty-state" className="text-center py-12 text-foreground-muted border border-border rounded-lg bg-background-secondary">
          <Video size={32} className="mx-auto mb-2 opacity-50" />
          <p>{t.groupTabs.noVideosYet}</p>
          {canEdit && <p className="text-sm mt-1">{t.groupTabs.addFirstVideo}</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="group-videos-tab-video-tiles">
          {videos.map((video, index) => {
            const embedUrl = getVideoEmbedUrl(video.url);
            return (
              <div key={index} data-testid={`group-videos-tab-tile-${index}`} className="relative border border-border rounded-lg overflow-hidden bg-white">
                {embedUrl ? (
                  <iframe
                    src={embedUrl}
                    title={video.title || `Video ${index + 1}`}
                    className="w-full aspect-video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className="w-full aspect-video flex items-center justify-center bg-background-secondary">
                    <p className="text-foreground-muted">{t.groupTabs.invalidVideoUrl}</p>
                  </div>
                )}
                {video.title && <p className="p-2 text-sm font-medium text-foreground">{video.title}</p>}
                {canEdit && (
                  <button
                    onClick={() => handleDeleteVideo(index)}
                    data-testid={`group-videos-tab-delete-btn-${index}`}
                    className="absolute top-2 right-2 p-1.5 bg-error text-white rounded-full hover:bg-error/90 transition"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="group-videos-tab-modal-root">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="relative bg-background rounded-lg shadow-lg w-full max-w-md mx-4 p-6">
            <button onClick={() => setShowModal(false)} data-testid="group-videos-tab-modal-close-btn" className="absolute top-4 right-4 text-foreground-muted hover:text-foreground">
              <X size={20} />
            </button>
            <h2 className="text-lg font-semibold text-foreground mb-4">{t.groupTabs.addVideo}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t.groupTabs.youtubeUrl} *</label>
                <input
                  type="text"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://www.youtube.com/... or https://vimeo.com/..."
                  data-testid="group-videos-tab-modal-url-input"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t.groupTabs.titleOptional}</label>
                <input
                  type="text"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  placeholder={t.groupTabs.videoTitle}
                  data-testid="group-videos-tab-modal-title-input"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:border-primary"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)} data-testid="group-videos-tab-modal-cancel-btn" className="px-4 py-2 text-sm font-medium text-foreground hover:bg-background-secondary rounded-lg transition-colors">
                {t.common.cancel}
              </button>
              <button
                onClick={handleAddVideo}
                disabled={!videoUrl.trim() || submitting}
                data-testid="group-videos-tab-modal-submit-btn"
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? t.groupTabs.adding : t.groupTabs.addVideo}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

